import { AbstractControl, UntypedFormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AnyType } from '@thehubz/shared/model';

/**
 * This ValidatorFn checks for duplicate selections in a list of rows
 * @returns
 */
export function duplicateSelectionValidator(excludeProps: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rows = control.value;
    rows.map((row: AnyType) => {
      excludeProps.forEach((prop) => {
        delete row[prop];
      });
      return row;
    });

    // If there are less than 2 rows or no rows at all, there can't be any duplicates
    if (!rows || rows.length < 2) {
      return null;
    }

    const duplicateIndices: number[] = [];

    // Iterate through each pair of rows and compare them
    for (let i = 0; i < rows.length; i++) {
      for (let j = i + 1; j < rows.length; j++) {
        if (compareObjects(rows[i], rows[j])) {
          duplicateIndices.push(i, j);
        }
      }
    }
    const totalControls = (control as AnyType as UntypedFormArray).length;
    let duplicatesFound = false; // Variable to track if duplicates are found

    // Set errors for duplicate indices if any duplicates are found
    if (duplicateIndices.length > 0) {
      duplicatesFound = true; // Set the flag to true if duplicates are found
      duplicateIndices.forEach((index) => {
        (control as AnyType as UntypedFormArray).at(index).setErrors({ duplicate: true });
      });
    }

    // Clear errors for non-duplicate indices
    for (let i = 0; i < totalControls; i++) {
      if (!duplicateIndices.includes(i)) {
        (control as AnyType as UntypedFormArray).at(i).setErrors(null);
      }
    }

    // Return appropriate value based on whether duplicates are found or not
    return duplicatesFound ? { duplicates: true } : null;
  };
}

/**
 * Function to compare two objects based on their properties
 * @param obj1 First object
 * @param obj2 Second object
 * @returns True if the objects are equal, false otherwise
 */
function compareObjects(obj1: AnyType, obj2: AnyType): boolean {
  const keys1 = Object.entries(obj1)
    .filter(([key, value]) => value !== '' && value !== null && value !== undefined && (typeof value !== 'object' || Object.keys(value).length > 0))
    .map(([key]) => key);
  const keys2 = Object.entries(obj2)
    .filter(([key, value]) => value !== '' && value !== null && value !== undefined && (typeof value !== 'object' || Object.keys(value).length > 0))
    .map(([key]) => key);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!obj1[key] || !obj2[key]) return false;
    if (obj1[key] !== obj2[key]) {
      const firstValue = obj1[key]._id || obj1[key];
      const secondValue = obj2[key]._id || obj2[key];
      if (firstValue !== secondValue) {
        return false;
      }
    }
  }

  return true;
}
